/**
 * Splitting a cart line's id back into the product it belongs to and the tier it
 * selects.
 *
 * WHY THIS IS SHARED. The cart page and the checkout page each grew their own
 * copy of this parse, they disagreed, and each one was right about a case the
 * other got wrong:
 *
 *   - The cart's version handled `prod-XXX` correctly but had no rule at all for
 *     a modern tiered line whose stored `tierId` field was missing, so it looked
 *     up `AbC123-1755000000000` as a whole and found nothing. Those rows silently
 *     lost their seller link, refund label and file summary, and were skipped by
 *     the "price changed since you added it" check — the one line most likely to
 *     have been repriced, because it is the oldest.
 *   - The checkout's version handled that case but split ANY hyphenated id at
 *     its last hyphen, so a legacy NON-tiered `prod-XXX` line resolved to the
 *     product `prod`, and checkout failed with "Product prod is no longer
 *     available."
 *
 * `tierId` only became a field on the cart line in the recurring-tiers commit,
 * and the cart is persisted with no expiry (Firestore when signed in,
 * localStorage otherwise), so lines predating it are still out there and are
 * exactly the ones this has to get right.
 *
 * THE INVARIANT THAT MAKES THE GUESSING SAFE: a product document id never
 * contains a hyphen. Firestore auto-ids are alphanumeric; imported ids are
 * `imp_<source>_<uid>_<sourceId>` with every part stripped to alphanumerics (see
 * sanitizeIdPart in functions/src/import-export.ts) so they use underscores; and
 * the one historical exception is the `prod-XXX` scheme, which is why it gets
 * its own branch instead of being guessed at. Tier ids are `Date.now()` numbers,
 * which contain no hyphen either.
 *
 * Keep this in step with the server: createPaymentIntent prices each line from
 * the product document using the `productId` / `tierId` the checkout sends, so a
 * parse that disagrees is a failed checkout or a mispriced row.
 */

/**
 * Hard ceiling on cart lines. Lives here, beside the line-id parse, so the cart
 * provider and the payment-link planner (src/lib/payment-link.ts) read one
 * number: a link that offered "add to your cart" on a full cart would be
 * refused by addToCart with nothing on the link's page to say why.
 */
export const MAX_CART_ITEMS = 10;

export type ParsedCartLineId = {
  /** The product document this line belongs to. */
  productId: string;
  /** The selected tier, or undefined for a non-tiered line. */
  tierId?: string;
};

const LEGACY_PREFIX = 'prod-';

/**
 * Resolve a cart line id (plus the line's explicit `tierId`, when it has one)
 * into the product and tier it addresses.
 *
 * The explicit field wins whenever it is present and actually matches the id's
 * suffix, because that is the only branch that needs no guessing at all.
 */
export function parseCartLineId(id: string, tierId?: string): ParsedCartLineId {
  const rawId = String(id ?? '');

  // 1. Explicit tier field. Unambiguous: strip exactly the suffix it names.
  if (tierId && rawId.endsWith(`-${tierId}`)) {
    return { productId: rawId.slice(0, -(tierId.length + 1)), tierId };
  }

  // 2. Legacy `prod-XXX` ids, which contain a hyphen of their own. Three or more
  //    segments means a tier is appended; exactly two means the id IS the
  //    product and must be left whole.
  if (rawId.startsWith(LEGACY_PREFIX)) {
    const parts = rawId.split('-');
    if (parts.length > 2) {
      return {
        productId: parts.slice(0, 2).join('-'),
        tierId: tierId || parts.slice(2).join('-'),
      };
    }
    return { productId: rawId, ...(tierId ? { tierId } : {}) };
  }

  // 3. Everything else. The product id cannot contain a hyphen, so the FIRST one
  //    is the tier boundary — and taking the first rather than the last keeps a
  //    tier id that somehow contains a hyphen in one piece.
  const cut = rawId.indexOf('-');
  if (cut > 0) {
    return { productId: rawId.slice(0, cut), tierId: tierId || rawId.slice(cut + 1) };
  }

  return { productId: rawId, ...(tierId ? { tierId } : {}) };
}

/** The product document a cart line belongs to. */
export function cartLineProductId(id: string, tierId?: string): string {
  return parseCartLineId(id, tierId).productId;
}

/** The tier a cart line selects, from the field or the composite id. */
export function cartLineTierId(id: string, tierId?: string): string | undefined {
  return parseCartLineId(id, tierId).tierId;
}

/**
 * A cart line's price as a number, or null when it is not one.
 *
 * Tier prices have been stored as text by the product editor ("12.50"), so a
 * tier line could arrive in the cart carrying a string, and the guest cart,
 * which keeps only numeric prices when it reloads from localStorage, dropped it
 * on the next page load (2026-10-26 audit, item 11). A numeric string is a
 * price; anything else is not. What checkout charges never comes from here:
 * the server prices every line from the product.
 *
 * @param value The line's stored price.
 */
export function cartLinePrice(value: unknown): number | null {
  const n = typeof value === 'number' ? value
    : typeof value === 'string' && value.trim() !== '' ? Number(value)
    : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
}
